import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ArrowRight, Search, ShieldCheck, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/shop";
import { ProductGrid } from "@/components/site/ProductCard";
import { Empty, ErrorState, Loading } from "@/components/site/States";

const TABS = [
  ["all", "Semua"],
  ["consignment", "Consignment"],
  ["preowned", "Pre-Owned"],
];

const SORTS = [
  ["newest", "Terbaru"],
  ["price_asc", "Harga terendah"],
  ["price_desc", "Harga tertinggi"],
];

function Hero() {
  return (
    <section className="bg-[var(--bk-blue)] text-white border-b-2 border-neutral-900 relative overflow-hidden" data-testid="collection-gallery-hero">
      <div className="absolute inset-0 halftone opacity-20" />
      <div className="relative container-x py-10 sm:py-14 grid md:grid-cols-[1fr_auto] gap-6 items-end">
        <div>
          <span className="inline-flex items-center gap-2 bg-[var(--bk-yellow)] text-neutral-900 border-2 border-neutral-900 rounded-full px-3 py-1 text-xs font-bold"><Tag className="h-3.5 w-3.5" />Koleksi Titipan & Pre-Owned</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">Koleksi Titipan</h1>
          <p className="mt-3 max-w-2xl text-white/90">Barang koleksi dari sesama kolektor dan barang pre-owned pilihan. Setiap barang sudah dicek tim BengkelHobby, kondisinya dijelaskan di halaman produk, dan bisa dibeli lewat checkout biasa.</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[var(--bk-yellow)]" />Diinspeksi sebelum tayang</span>
            <span className="inline-flex items-center gap-1.5"><Tag className="h-4 w-4 text-[var(--bk-yellow)]" />Label CONSIGNMENT / PRE-OWNED jelas</span>
          </div>
        </div>
        <Link to="/jual-koleksi" className="btn-brand bg-white text-neutral-900" data-testid="collection-gallery-sell-cta">Mau titip koleksimu? <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
}

export default function CollectionGallery() {
  useSeo("Koleksi Titipan & Pre-Owned — Bengkel Toys & Hobbies", "Barang koleksi consignment dan pre-owned pilihan di Bengkel Toys & Hobbies Pontianak, sudah diinspeksi dan kondisinya dijelaskan.");
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("jenis") || "all";
  const sort = sp.get("sort") || "newest";
  const qParam = sp.get("q") || "";
  const [q, setQ] = useState(qParam);
  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n, { replace: true });
  };
  const query = useInfiniteQuery({
    queryKey: ["collection-gallery", tab, sort, qParam],
    queryFn: ({ pageParam = 1 }) => api.get("/products", { params: { collection: tab, sort, q: qParam, page: pageParam, limit: 20 } }).then((r) => r.data),
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
    initialPageParam: 1,
  });
  const items = query.data?.pages.flatMap((p) => p.items) || [];
  const total = query.data?.pages[0]?.total ?? 0;
  return (
    <div data-testid="collection-gallery-page">
      <Hero />
      <div className="container-x py-8">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between mb-6">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" role="tablist">
            {TABS.map(([k, l]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParam("jenis", k === "all" ? "" : k)} data-testid={`collection-tab-${k}`}
                className={`shrink-0 rounded-full border-2 border-neutral-900 px-4 h-10 text-sm font-bold transition-colors ${tab === k ? "bg-neutral-900 text-white" : "bg-white hover:bg-[var(--bk-yellow)]"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <form onSubmit={(e) => { e.preventDefault(); setParam("q", q.trim()); }} className="relative flex-1 lg:w-72">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, merek, SKU..." className="w-full h-10 pl-9 pr-3 rounded-full border-2 border-neutral-900 text-sm" data-testid="collection-search-input" />
            </form>
            <select value={sort} onChange={(e) => setParam("sort", e.target.value === "newest" ? "" : e.target.value)} className="h-10 rounded-full border-2 border-neutral-900 px-3 text-sm bg-white" data-testid="collection-sort-select">
              {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        {!query.isLoading && !query.isError && <p className="text-sm text-neutral-600 mb-4" data-testid="collection-total">{total} barang ditemukan</p>}
        {query.isLoading ? <Loading /> : query.isError ? <ErrorState onRetry={() => query.refetch()} /> : items.length ? (
          <>
            <ProductGrid items={items} />
            {query.hasNextPage && (
              <div className="text-center mt-8">
                <button onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage} className="btn-brand" data-testid="collection-load-more">{query.isFetchingNextPage ? "Memuat..." : "Muat lebih banyak"}</button>
              </div>
            )}
          </>
        ) : (
          <Empty title="Belum ada koleksi titipan" text={qParam ? "Coba kata kunci lain." : "Koleksi titipan & pre-owned akan tampil di sini setelah lolos inspeksi. Punya koleksi untuk dijual?"}
            action={<Link to="/jual-koleksi" className="btn-ink" data-testid="collection-empty-sell">Jual / Titip Koleksi</Link>} />
        )}
      </div>
    </div>
  );
}
